import { useEffect, useMemo, useRef, useState } from "react";
import { saveTyCode } from "../utils/tyCodeUtils";
import { getAndOpenNewTab, handleTabError } from "../../../common-deprecated/utils/pdfUtils";
import { UscCheckoutType } from "../../shared-logic/types/UscCommonTypes";
import { type UscActionsType, type UscStateType, useUscSelector } from "../redux/store";
import { openCheckout } from "../utils/helpers";
import { useStore } from "../../../shared-logic/redux/store";

/**
 * Helper for when you need to execute certain logic that uses a saved tyCode on click.
 * The hook will call the provided callback with the tyCode once fetched (or immediately when cached).
 *
 * @param callback function that will be executed once the tycode is available.
 * @param will open a new tab and add it in the callback if no tycode was provided
 */
const useSaveTyCode = (
    callback: (tyCode: string, tab?: Window | null) => void,
    openNewTab?: boolean,
): { loading: boolean; wrappedCallback: () => void } => {
    const { getState, dispatch } = useStore<UscStateType, UscActionsType>();
    const save = useUscSelector((state) => state.save);
    const commentSettings = useUscSelector((state) => state.commonSettings);

    const openTab = useRef<Window | null>(null);
    const [loading, setLoading] = useState(false);
    const { data, failed } = save;
    const tyCode = data?.code || "";

    // save the code if it hasn't been fetched yet or execute immediately if it has been
    const wrappedCallback = (): void => {
        if (tyCode) {
            callback(tyCode);
        } else {
            if (openNewTab && !openTab.current) openTab.current = getAndOpenNewTab();
            setLoading(true);
            saveTyCode(getState, dispatch);
        }
    };

    // execute callback once toyota code is loaded
    useEffect(() => {
        if (loading && tyCode) {
            // check if this hook instance was the one who requested tyCode
            setLoading(false);
            callback(tyCode, openTab.current);
        }
    }, [tyCode]);

    // handle errors when saving tyCode failed
    useEffect(() => {
        if (failed) setLoading(false);
        if (failed && openNewTab && openTab.current) {
            handleTabError("Saving tyCode failed", null, commentSettings, openTab.current);
        }
    }, [failed]);

    return useMemo(() => ({ loading, wrappedCallback }), [loading, callback]);
};

/**
 * This is a temporary workaround until there is a way to handle saving without using hooks.
 */
export const useCheckoutCallback = (): (() => void) => {
    const vehicle = useUscSelector((state) => state.vehicle);
    const activeFinanceOption = useUscSelector((state) => state.basket.activeFinanceOption);
    const checkoutType = useUscSelector((state) => state.basket.checkoutType);

    const checkoutLink = useSaveTyCode((tyCode: string) =>
        openCheckout(
            vehicle.result?.id || "",
            tyCode,
            activeFinanceOption,
            vehicle.type,
            checkoutType === UscCheckoutType.Reserve,
        ),
    );
    return checkoutLink.wrappedCallback;
};

export default useSaveTyCode;
